import {
  OPERATIONAL_GRANT_ALL_PERMISSIONS,
  OPERATIONAL_MODULES,
  OPERATIONAL_MODULE_IDS,
} from './operationalAccessRegistry.js';
import { MASTER_MODULES, MASTER_MODULE_IDS } from './masterGovernanceRegistry.js';
import { resolveMasterAccess, isMasterDataPath } from './resolveMasterAccess.js';
import { canonicalPermissionKey, normalizeModuleId, normKey } from './operationalModuleAliases.js';
import { applyPhase1ModuleExclusions } from './phase1ModuleScope.js';
import { permissionKeysMatch } from './permissionKeyMatching.js';
import { extractAppsPrefix, replaceTemplatePrefix } from './governance/tenantGovernanceIdentity.js';
import { UI_SHELL } from './workflowRoutes.js';
import { WORKFLOW_PERMISSION_KEYS } from './workflowPermissionKeys.js';

const KNOWN_MODULE_IDS = new Set([...OPERATIONAL_MODULE_IDS, ...MASTER_MODULE_IDS]);
const TENANT_APPS_PREFIX_RE = /^(FONE|CHERITTO|REG[0-9A-Z]+)$/i;

function pathMatchesPattern(pathname, pattern) {
  const p = String(pathname ?? '').trim();
  const pat = String(pattern ?? '').trim();
  if (!pat) return false;
  if (pat.endsWith('/*')) {
    const prefix = pat.slice(0, -2);
    return p === prefix || p.startsWith(`${prefix}/`);
  }
  if (pat.includes('*')) {
    const escaped = pat.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\\\.\*/g, '.*');
    return new RegExp(`^${escaped}$`).test(p);
  }
  const escaped = pat.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escaped}$`).test(p);
}

function anyUriMatch(uriList, patterns) {
  if (!Array.isArray(uriList) || !uriList.length || !patterns?.length) return false;
  return uriList.some((uri) => {
    // REST permission patterns are not desks. OPBILLINGCOUNTER:API must not open OP billing.
    if (String(uri).includes("*")) return false;
    return patterns.some((pat) => {
      if (pathMatchesPattern(uri, pat)) return true;
      const prefix = String(pat).replace(/\.\*$/, '');
      if (prefix !== pat && (uri === prefix || uri.startsWith(`${prefix}/`))) {
        return true;
      }
      return false;
    });
  });
}

function anyPathMatch(pathname, patterns) {
  if (!pathname || !patterns?.length) return false;
  return patterns.some((pat) => pathMatchesPattern(pathname, pat));
}

function collectMenuUris(menuItems, out = []) {
  if (!Array.isArray(menuItems)) return out;
  for (const menu of menuItems) {
    if (!menu || typeof menu !== 'object') continue;
    if (menu.uri) out.push(String(menu.uri).trim());
    const l1 = menu.menuLevelOne ?? menu.menuLevelOneItems;
    if (Array.isArray(l1)) {
      for (const item of l1) {
        if (item?.uri) out.push(String(item.uri).trim());
        const l2 = item?.menuLevelTwo;
        if (Array.isArray(l2)) {
          for (const sub of l2) {
            if (sub?.uri) out.push(String(sub.uri).trim());
          }
        }
      }
    }
  }
  return out;
}

function permissionMatch(permissions, keys = [], prefixes = []) {
  if (!permissions?.length) return false;
  for (const k of keys) {
    if (permissions.some((p) => permissionKeysMatch(k, p))) return true;
    // Tenant-scoped keys must keep their appsCode. Stripping to bare SHIFTOPEN/etc.
    // would let AppCode A grants satisfy AppCode B checks.
    const appsPrefix = extractAppsPrefix(k);
    if (appsPrefix && TENANT_APPS_PREFIX_RE.test(appsPrefix)) {
      continue;
    }
    const canonical = canonicalPermissionKey(k);
    if (canonical !== k && permissions.some((p) => permissionKeysMatch(canonical, p))) {
      return true;
    }
  }
  for (const prefix of prefixes) {
    const canonicalPrefix = canonicalPermissionKey(prefix);
    if (
      permissions.some(
        (p) => permissionKeysMatch(prefix, p) || permissionKeysMatch(canonicalPrefix, p),
      )
    ) {
      return true;
    }
    const px = normKey(prefix);
    if (!px) continue;
    for (const p of permissions) {
      if (normKey(p).includes(px)) return true;
    }
  }
  return false;
}

function pageAccessMatch(accessControlList, pageNames = []) {
  if (!pageNames?.length || !Array.isArray(accessControlList) || !accessControlList.length) return false;
  const names = new Set(pageNames.map((n) => String(n).trim()));
  return accessControlList.some((entry) => {
    const page = entry?.pageName ?? entry?.page_name;
    const type = String(entry?.accessType ?? entry?.access_type ?? '').toLowerCase();
    return names.has(page) && (type === 'read' || type === 'read/write');
  });
}

function grantAllFromAdmin(permissions) {
  return permissionMatch(permissions, OPERATIONAL_GRANT_ALL_PERMISSIONS, OPERATIONAL_GRANT_ALL_PERMISSIONS);
}

/** Desks that call assertPharmacyProcure / assertPharmacyGrn on first list fetch. */
const URI_REQUIRES_WORKFLOW_MODULE_IDS = new Set(['PHARMACY_PROCURE', 'PHARMACY_GRN']);

/**
 * Workflow keys are stored as FONE template tokens. Hospital sessions grant
 * `{appsCode}:PHARMACYPROCURE`, and permission matching rejects a different
 * tenant prefix. Rewrite FONE → the signed-in appsCode before the check,
 * matching PharmacyWorkflowPermissionSupport.tenantScopePermissionKeys.
 */
const WORKFLOW_SUFFIXES = new Set(
  Object.values(WORKFLOW_PERMISSION_KEYS).map((key) => String(key).split(':').pop().toUpperCase()),
);

/**
 * A grant such as HOSP_A:REFUNDREVIEW belongs to one hospital. FONE:REFUNDREVIEW belongs
 * to the master template only. DOCUMENTAI:API is a permission family, not an Appscode.
 */
function grantAppliesToAppsCode(granted, appsCode) {
  const prefix = extractAppsPrefix(granted);
  if (!prefix) return true;
  const suffix = String(granted).slice(String(granted).indexOf(':') + 1).toUpperCase();
  const appsScoped = TENANT_APPS_PREFIX_RE.test(prefix) || WORKFLOW_SUFFIXES.has(suffix);
  if (!appsScoped) return true;
  const code = typeof appsCode === 'string' ? appsCode.trim() : '';
  // Callers that omit appsCode are matching a single session grant list.
  // Once the signed-in Appscode is known, another hospital or the FONE template does not apply.
  if (!code) return true;
  return prefix.toUpperCase() === code.toUpperCase();
}

function grantsForAppsCode(permissions, appsCode) {
  return permissions.filter((granted) => grantAppliesToAppsCode(granted, appsCode));
}

function workflowKeysGranted(permissions, workflowKeys, appsCode) {
  if (!Array.isArray(workflowKeys) || workflowKeys.length === 0) return false;
  if (permissionMatch(permissions, workflowKeys, [])) return true;
  const code = typeof appsCode === 'string' ? appsCode.trim() : '';
  if (!code) return false;
  const scoped = workflowKeys.map((key) => replaceTemplatePrefix(code, key));
  return permissionMatch(permissions, scoped, []);
}

function resolveModule(moduleDef, ctx) {
  if (ctx.grantAll) return true;
  if (
    permissionMatch(ctx.permissions, moduleDef.permissionKeys, moduleDef.permissionPrefixes)
  ) {
    return true;
  }
  const byUri = anyUriMatch(ctx.uriList, moduleDef.uriPatterns);
  const byPage = pageAccessMatch(ctx.accessControlList, moduleDef.pageNames);
  if (!byUri && !byPage) return false;

  // URI/page alone must not open procure/GRN desks — PharmacySupplierPage mounts
  // searchPhmSuppliers → assertPharmacyProcure → PHM_GOV_FORBIDDEN without JWT keys.
  if (URI_REQUIRES_WORKFLOW_MODULE_IDS.has(moduleDef.id)) {
    const workflowKeys = moduleDef.workflowPermissionKeys;
    if (Array.isArray(workflowKeys) && workflowKeys.length > 0) {
      return workflowKeysGranted(ctx.permissions, workflowKeys, ctx.appsCode);
    }
  }
  return true;
}

/**
 * Derive operational module access from admin-service session grants only.
 * @param {object} ctx
 * @param {string[]} [ctx.permissions]
 * @param {string[]} [ctx.uriList]
 * @param {Array} [ctx.menuItems]
 * @param {Array} [ctx.accessControlList]
 * @param {Map<string, boolean>} [ctx.serverModules] optional auth-service precomputed map
 * @param {string} [ctx.shell]
 */
export { normalizeModuleId, canonicalPermissionKey } from './operationalModuleAliases.js';

/** Map a healthcare-ui route to the owning operational module (if registered). */
export function resolveModuleIdForPath(pathname) {
  if (!pathname) return null;
  for (const id of OPERATIONAL_MODULE_IDS) {
    const def = OPERATIONAL_MODULES[id];
    if (anyPathMatch(pathname, def.uriPatterns) || anyPathMatch(pathname, def.menuUriPatterns)) {
      return id;
    }
  }
  return null;
}

export function resolveOperationalAccess(ctx = {}) {
  const appsCode = typeof ctx.appsCode === 'string' ? ctx.appsCode.trim() : '';
  const permissions = grantsForAppsCode(
    Array.isArray(ctx.permissions) ? ctx.permissions.filter(Boolean) : [],
    appsCode,
  );
  const uriList = Array.isArray(ctx.uriList) ? ctx.uriList.filter(Boolean) : [];
  const menuItems = ctx.menuItems ?? [];
  const accessControlList = ctx.accessControlList ?? [];
  const menuUris = collectMenuUris(menuItems);
  const shell = ctx.shell ?? UI_SHELL.HEALTHCARE_UI;
  const grantAll = grantAllFromAdmin(permissions);

  const rawServerModules = ctx.serverModules ?? ctx.operationalModules ?? null;
  const serverModules =
    rawServerModules && typeof rawServerModules === 'object'
      ? Object.fromEntries(
          Object.entries(rawServerModules).map(([k, v]) => [
            normalizeModuleId(k, KNOWN_MODULE_IDS),
            Boolean(v),
          ]),
        )
      : null;
  const modules = {};
  for (const id of OPERATIONAL_MODULE_IDS) {
    if (serverModules && id in serverModules) {
      modules[id] = Boolean(serverModules[id]);
    } else {
      modules[id] = resolveModule(OPERATIONAL_MODULES[id], {
        permissions,
        uriList,
        menuUris,
        menuItems,
        accessControlList,
        grantAll,
        appsCode,
      });
    }
    // Auth-service operationalModules can mark procure true from URI grants alone;
    // still require JWT workflow keys so SupplierPage does not 403 on mount.
    if (modules[id] && URI_REQUIRES_WORKFLOW_MODULE_IDS.has(id) && !grantAll) {
      const wf = OPERATIONAL_MODULES[id]?.workflowPermissionKeys;
      if (Array.isArray(wf) && wf.length > 0 && !workflowKeysGranted(permissions, wf, appsCode)) {
        modules[id] = false;
      }
    }
  }

  const scopedModules = applyPhase1ModuleExclusions(modules);

  const allowedRoutes = OPERATIONAL_MODULE_IDS.filter((id) => scopedModules[id])
    .map((id) => OPERATIONAL_MODULES[id].routes?.[shell])
    .filter(Boolean);

  const master = resolveMasterAccess({
    permissions,
    uriList,
    accessControlList,
    grantAll,
    shell,
  });

  const hasActionPermission = (keyOrKeys) => {
    if (grantAll) return true;
    const keys = (Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys]).filter(Boolean);
    if (!keys.length) return false;
    // Mirror billing OperationalContext: FONE:ACTION → {appsCode}:ACTION before match.
    // Cross-tenant prefixes never satisfy each other (AppCode A must not unlock AppCode B).
    const wanted = appsCode
      ? keys.map((key) => replaceTemplatePrefix(appsCode, key))
      : keys;
    return permissionMatch(permissions, wanted, []);
  };

  // Restricted actions (e.g. MAR recording): direct tenant grant only, grant-all does not satisfy.
  const hasExplicitActionPermission = (keyOrKeys) => {
    const keys = (Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys]).filter(Boolean);
    if (!keys.length) return false;
    const wanted = appsCode ? keys.map((key) => replaceTemplatePrefix(appsCode, key)) : keys;
    return permissionMatch(permissions, wanted, []);
  };

  return {
    modules: scopedModules,
    masterModules: master.modules,
    grantAll,
    allowedRoutes: [...allowedRoutes, ...master.allowedRoutes],
    menuUris,
    canAccessMasterModule: master.canAccessMasterModule,
    canAccessModule: (moduleId) =>
      Boolean(scopedModules[moduleId]) || master.canAccessMasterModule(moduleId),
    hasActionPermission,
    hasExplicitActionPermission,
    canAccessPath: (pathname) => {
      if (grantAll) return true;
      if (!pathname) return false;
      if (master.canAccessPath(pathname)) return true;
      // Master/setup routes must not pass via raw uriList — legacy menu URIs are resolved in resolveMasterAccess.
      if (isMasterDataPath(pathname, shell)) return false;
      const owningModule = resolveModuleIdForPath(pathname);
      // Procure/GRN: module denial must win over raw uriList (otherwise SupplierPage mounts
      // and searchPhmSuppliers hits assertPharmacyProcure).
      if (
        owningModule &&
        URI_REQUIRES_WORKFLOW_MODULE_IDS.has(owningModule) &&
        !scopedModules[owningModule]
      ) {
        return false;
      }
      return (
        OPERATIONAL_MODULE_IDS.some((id) => {
          if (!scopedModules[id]) return false;
          const def = OPERATIONAL_MODULES[id];
          return (
            anyPathMatch(pathname, def.uriPatterns) || anyPathMatch(pathname, def.menuUriPatterns)
          );
        }) || anyUriMatch(uriList, [pathname, `${pathname}.*`])
      );
    },
  };
}

/** @deprecated use resolveOperationalAccess(...).hasActionPermission — kept for direct imports */
export function hasWorkflowActionPermission(permissions, keyOrKeys, grantAll = false) {
  if (grantAll || permissionMatch(permissions, [WORKFLOW_PERMISSION_KEYS.GRANT_ALL], [])) {
    return true;
  }
  const keys = (Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys]).filter(Boolean);
  if (!keys.length) return false;
  return permissionMatch(permissions, keys, []);
}
